import React, {useMemo, useState} from "react";
import {NavLink} from "react-router-dom";
import PaginatedFlatTable from "../../pagination/PaginatedFlatTable";
import InitialsAvatar from "../../../util/name_initial";
import useThemeColors from "../../organization/portal/useThemeColors";
import useTranslation from "../../../i18n/useTranslation";

export default function OrgPortalTeamsTab({
                                              teams = [],
                                              division,
                                              activeSeasonData,
                                          }) {
    const colors = useThemeColors();
    const {t} = useTranslation();

    const [page, setPage] = useState(1);
    const PAGE_SIZE = 20;

    const safeTeams = Array.isArray(teams) ? teams : [];

    const totalCount = safeTeams.length;
    const pages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    const pageStart = (page - 1) * PAGE_SIZE;
    const pageEnd = pageStart + PAGE_SIZE;

    const ui = useMemo(() => {
        return {
            row: {
                display: "flex",
                alignItems: "center",
                gap: 10,
            },
            name: {
                fontSize: 14,
                fontWeight: 900,
                color: "#14151a",
            },
            link: {
                color: "#14151a",
                textDecoration: "none",
            },
            muted: {
                fontSize: 12,
                fontWeight: 700,
                color: "rgba(20,21,26,0.6)",
            },
            avatar: {
                width: 42,
                height: 42,
                borderRadius: 10,
                objectFit: "cover",
                border: "1px solid rgba(0,0,0,0.08)",
            },
            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 800,
                background: "rgba(0,0,0,0.03)",
                border: "1px solid rgba(0,0,0,0.08)",
                marginLeft: "auto",
            },
        };
    }, []);

    return (
        <PaginatedFlatTable
            title={t("common.teams")}
            subtitle={t("teamList.title")}
            page={page}
            pages={pages}
            items={safeTeams}
            onPageChange={setPage}
            headers={[
                t("teamList.headers.team"),
                t("teamList.headers.players"),
            ]}
            renderRow={(seasonTeam, idx) => {
                if (idx < pageStart || idx >= pageEnd) return null;

                const team = seasonTeam?.team || seasonTeam;

                // 🔴 SAFE GUARDS (this fixes your crash)
                const slug = team?.slug;
                const name = seasonTeam?.teamName || team?.name || "Unnamed Team";
                const playersCount = team?.players?.length || 0;
                const logo = team?.logoUrl || null;

                return (
                    <tr key={seasonTeam?.teamId || slug || idx}>
                        <td>
                            <div style={ui.row}>
                                {logo ? (
                                    <img src={logo} alt="team" style={ui.avatar}/>
                                ) : (
                                    <InitialsAvatar
                                        name={name}
                                        size={42}
                                        backgroundColor={colors.primary}
                                        textColor={colors.textOnPrimary}
                                    />
                                )}

                                <div>
                                    <div style={ui.name}>
                                        {slug ? (
                                            <NavLink
                                                to={`/team/${slug}`}
                                                state={{
                                                    team: seasonTeam,
                                                    division,
                                                    activeSeasonData,
                                                }}
                                                style={ui.link}
                                            >
                                                {name}
                                            </NavLink>
                                        ) : (
                                            name
                                        )}
                                    </div>

                                    <div style={ui.muted}>
                                        {division?.organization?.name}
                                    </div>
                                </div>
                            </div>
                        </td>

                        <td style={{
                            display: "flex",
                            justifyContent: "flex-end",
                        }}>
                            <span style={ui.chip}>
                                <i className="fas fa-users"/>
                                {playersCount}
                            </span>
                        </td>
                    </tr>
                );
            }}
        />
    );
}